import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';

import { PushActionButton, PushContentDefinition } from 'modules/graphql/types';
import { useGetTemplateUserJsonTestData } from 'modules/templateEditor/hooks/useGetTemplateUserJsonTestData';

import { PreviewWithDataErrorMessage } from '../../common/types';
import { getContentWithData, getPreviewWithDataError } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { PushPreviewContext, PushPreviewContextState } from './PushPreviewContext';

export const PushPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { canRefreshPreview, dataFeedData, userData, isUserDataDirty, setIsUserDataDirty } =
    usePreviewWithDataContext();
  const { templateId } = useQueryParamContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageAttachmentUrlAndroid, setMessageAttachmentUrlAndroid] = useState<string>('');
  const [messageAttachmentUrlIos, setMessageAttachmentUrlIos] = useState<string>('');
  const [messageContentBody, setMessageContentBody] = useState<string>('');
  const [messageContentTitle, setMessageContentTitle] = useState<string>('');
  const [messageActionButtons, setMessageActionButtons] = useState<PushActionButton[]>([]);
  const [messageRawDataPayload, setMessageRawDataPayload] = useState<string>('{}');
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const hasContent = !isEmpty(pushData);
  const { actionButtons, attachmentUrlAndroid, attachmentUrlIos, message, title, payload } =
    pushData;
  const [previewError, setPreviewError] = useState<PreviewWithDataErrorMessage | undefined>(
    undefined,
  );
  const locale = pushData?.locale?.name || '';

  const { getTemplateUserJsonTestData } = useGetTemplateUserJsonTestData();

  const onRefreshPreview = useCallback(async () => {
    // Only make request when template data has been received
    if (canRefreshPreview && hasContent) {
      setIsLoading(true);

      const userDataToSave = await getTemplateUserJsonTestData(userData, isUserDataDirty);

      getContentWithData(
        templateId,
        {
          subject: '',
          pushAttachmentUrlAndroid: attachmentUrlAndroid ?? '',
          pushAttachmentUrlIos: attachmentUrlIos ?? '',
          pushBody: message ?? '',
          pushTitle: title ?? '',
          pushButtons: actionButtons ?? [],
          payload: JSON.parse(payload ?? '{}'),
        },
        JSON.parse(dataFeedData),
        JSON.parse(userDataToSave),
      )
        .then(({ data }) => {
          setIsUserDataDirty(false);
          setMessageAttachmentUrlAndroid(data.pushAttachmentUrlAndroid);
          setMessageAttachmentUrlIos(data.pushAttachmentUrlIos);
          setMessageContentBody(data.pushBody);
          setMessageContentTitle(data.pushTitle);
          setMessageActionButtons(data.pushButtons);
          setMessageRawDataPayload(JSON.stringify(data.payload));
          setPreviewError(undefined);
        })
        .catch(({ response }) => {
          setPreviewError(getPreviewWithDataError(response?.data?.message || ''));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    attachmentUrlAndroid,
    attachmentUrlIos,
    canRefreshPreview,
    message,
    title,
    payload,
    dataFeedData,
    templateId,
    userData,
    hasContent,
    isUserDataDirty,
    setIsUserDataDirty,
    getTemplateUserJsonTestData,
    actionButtons,
  ]);

  /* Note: we only want this useEffect to run when dataFeedData or userData change even though it is
   * also dependent on onRefreshPreview. However, onRefreshPreview shares ALL of the useEffect's
   * dependencies, so the version of onRefreshPreview used will always be current.
   * Including onRefreshPreview as a dependency here would cause an unintended side-effect of
   * running onRefreshPreview on each change of fullSmsMessage */
  useEffect(() => {
    if (canRefreshPreview) {
      onRefreshPreview();
    }
  }, [canRefreshPreview, dataFeedData, userData, locale]);

  const context: PushPreviewContextState = {
    isLoading,
    messageAttachmentUrlAndroid,
    messageAttachmentUrlIos,
    messageContentBody,
    messageContentTitle,
    messageActionButtons,
    messageRawDataPayload,
    onRefreshPreview,
    previewError,
  };

  return <PushPreviewContext.Provider value={context}>{children}</PushPreviewContext.Provider>;
};
